import React, { useState } from 'react';
import {
  Box,
  Button,
  MenuItem,
  Typography,
  TextField,
  Modal,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { motion, AnimatePresence } from 'framer-motion';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

function EnterDetailsManually({ open, onClose, initialVin }) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    vin: initialVin || '',
    category: '',
    customCategory: '',
    notes: '',
    cost: '',
    date_occurred: new Date(),
    service_provider: '',
    comments: '',
    scannedVins: [],
  });

  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [vinMismatch, setVinMismatch] = useState(false);

  const categories = [
    'Sales Tax & DMV tag and Title',
    'Floor Fee',
    'Shipment Fee',
    'Body/paint cost',
    'Parts for Body',
    'Rust/Welding',
    'Upholstery',
    'Glass',
    'Mechanic Cost',
    'Parts for Mechanic',
    'Tires',
    'Battery',
    'Inspection & Emission Cost',
    'Detailing Cost',
    'Parts',
    'Custom',
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'category') {
      setIsCustomCategory(value === 'Custom');
    }

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      date_occurred: date,
    });
  };

  const formatDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const username = localStorage.getItem('username');

    const reportData = {
      ...formData,
      category: isCustomCategory ? formData.customCategory : formData.category,
      date_occurred: formatDate(formData.date_occurred),
      username,
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/insert_report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData),
      });
      
      const data = await response.json();
      
      if (data.error) {
        alert('Error: ' + data.error);
        setIsSubmitting(false);
      } else {
        setShowSuccess(true);
        setTimeout(() => {
          onClose(true);
          localStorage.setItem('refreshDealCost', 'true');
          navigate(`/view-deal/${formData.vin}`);
        }, 1500);
      }
    } catch (error) {
      console.error('Error:', error);
      setIsSubmitting(false);
    }
  };

  const handleScanDocument = async (event) => {
    if (!event.target.files || !event.target.files[0]) return;

    setIsScanning(true);
    const file = event.target.files[0];
    const formData = new FormData();
    formData.append('image', file);

    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/process-image`, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Server response:', errorText);
            throw new Error('Failed to process image');
        }

        const data = await response.json();
        
        // Parse the date string to a valid Date object
        let parsedDate;
        if (data.date) {
            try {
                // Split the date string and rearrange to ensure proper format
                const [month, day, year] = data.date.split('/');
                parsedDate = new Date(year, month - 1, day);
                
                // Check if date is valid
                if (isNaN(parsedDate.getTime())) {
                    parsedDate = new Date(); // fallback to current date if invalid
                }
            } catch (e) {
                console.error('Error parsing date:', e);
                parsedDate = new Date(); // fallback to current date
            }
        } else {
            parsedDate = new Date(); // fallback to current date if no date provided
        }
        
        // Check for VIN mismatches
        const foundVins = data.vins || [];
        const currentVin = formData.vin;
        const mismatchedVins = foundVins.filter(vin => vin !== currentVin);
        
        if (mismatchedVins.length > 0) {
            setVinMismatch(true);
            setFormData(prevData => ({
                ...prevData,
                scannedVins: mismatchedVins
            }));
        } else {
            setVinMismatch(false);
            // Only update form if VINs match or no VINs found
            setFormData(prevData => ({
                ...prevData,
                date_occurred: parsedDate,
                cost: data.amount || '',
                notes: data.notes || '',
                category: data.category || '',
                service_provider: data.service_provider || ''
            }));
        }

    } catch (error) {
        console.error('Error during OCR scan:', error);
    } finally {
        setIsScanning(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="enter-details-modal">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: '600px',
              bgcolor: 'background.paper',
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
              overflowY: 'auto',
              maxHeight: '90vh',
            }}
          >
            {showSuccess ? (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '2rem',
                }}
              >
                <CheckCircleOutlineIcon 
                  sx={{ 
                    fontSize: 80, 
                    color: 'success.main',
                    mb: 2 
                  }} 
                />
                <Typography variant="h5" align="center" gutterBottom>
                  Report Submitted Successfully!
                </Typography>
              </motion.div>
            ) : (
              <>
                <Typography variant="h4" gutterBottom>
                  Enter Details Manually
                </Typography>

                {vinMismatch && (
                  <Box sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: '#fff3cd', 
                    border: '1px solid #ffeeba', 
                    borderRadius: 1,
                    mt: 2 
                  }}>
                    <Typography variant="body1" color="warning.main" gutterBottom>
                      Warning: Found VIN number(s) that do not match the current vehicle:
                    </Typography>
                    {formData.scannedVins && formData.scannedVins.map((vin, index) => (
                      <Typography key={index} variant="body2" color="warning.main" sx={{ ml: 2 }}>
                        • {vin}
                      </Typography>
                    ))}
                    <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                      Current vehicle VIN: {formData.vin}
                    </Typography>
                  </Box>
                )}

                <form onSubmit={handleSubmit}>
                  <TextField
                    label="VIN Number"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    name="vin"
                    value={formData.vin}
                    onChange={handleChange}
                    required
                    InputProps={{ readOnly: !!initialVin }}
                  />

                  <TextField
                    select
                    label="Category"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </TextField>

                  {isCustomCategory && (
                    <TextField
                      label="Custom Category"
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      name="customCategory"
                      value={formData.customCategory}
                      onChange={handleChange}
                      required
                    />
                  )}

                  <TextField
                    label="Description"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                  />
                  <TextField
                    label="Cost"
                    variant="outlined"
                    type="number"
                    fullWidth
                    margin="normal"
                    name="cost"
                    value={formData.cost}
                    onChange={handleChange}
                    required
                  />

                  <Typography variant="h6" gutterBottom>
                    Date Occurred
                  </Typography>
                  <DatePicker
                    selected={formData.date_occurred}
                    onChange={handleDateChange}
                    dateFormat="MM/dd/yyyy"
                    customInput={<TextField variant="outlined" fullWidth />}
                  />

                  <TextField
                    label="Service Provider"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    name="service_provider"
                    value={formData.service_provider}
                    onChange={handleChange}
                    required
                  />

                  <Button
                    variant="contained"
                    component="label"
                    color="secondary"
                    fullWidth
                    style={{ marginBottom: '10px' }}
                    disabled={isScanning}
                  >
                    {isScanning ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CircularProgress size={24} sx={{ mr: 1 }} />
                            Scanning Document...
                        </Box>
                    ) : (
                        <>
                            Scan Document
                            <input type="file" hidden onChange={handleScanDocument} accept="image/*" />
                        </>
                    )}
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    disabled={isSubmitting}
                    sx={{ mt: 2 }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Submit Report'
                    )}
                  </Button>
                </form>
              </>
            )}

            {isScanning && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                }}
              >
                <CircularProgress size={60} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Processing Document...
                </Typography>
              </Box>
            )}
          </Box>
        </motion.div>
      </AnimatePresence>
    </Modal>
  );
}

export default EnterDetailsManually;